import { CircularProgress, IconButton, Tooltip } from '@mui/material';
import React from 'react';

/**
 * An action as an icon with its name in a tooltip, for headers and table rows where a row of
 * worded buttons crowds the content (owner, 2026-10-06: "use icon buttons wherever possible to
 * keep a minimal and compact view").
 *
 * The label is required and does three jobs: tooltip, accessible name and test hook, so an icon
 * can never ship nameless. Size comes from the theme's icon-button tokens (CLAUDE.md: 32px default,
 * `small` 28, `large` 44 touch); colour follows the button rules: default for ordinary actions,
 * `primary` for the main one, `error` for destructive. While `busy`, the icon is a spinner and the
 * button is disabled, so a slow action cannot be fired twice.
 */
export interface ActionIconButtonProps {
  label: string;
  icon: React.ReactNode;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  busy?: boolean;
  disabled?: boolean;
  color?: 'default' | 'primary' | 'error';
  size?: 'small' | 'medium' | 'large';
  className?: string;
}

const ActionIconButton: React.FC<ActionIconButtonProps> = ({
  label,
  icon,
  onClick,
  busy = false,
  disabled = false,
  color = 'default',
  size = 'medium',
  className,
}) => (
  <Tooltip title={label} arrow>
    {/* A disabled button fires no events, so the tooltip needs a wrapper to anchor to. */}
    <span className="cc-action-icon">
      <IconButton
        aria-label={label}
        color={color}
        size={size}
        className={className}
        disabled={disabled || busy}
        onClick={(e) => {
          e.stopPropagation();
          onClick(e);
        }}
      >
        {busy ? <CircularProgress size={16} color="inherit" /> : icon}
      </IconButton>
    </span>
  </Tooltip>
);

export default ActionIconButton;
